import React, { useEffect, useMemo, useState } from "react";
import {
  Briefcase,
  FolderOpen,
  Layers,
  Loader2,
  PencilLine,
  Plus,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import { QuizQuestion } from "./QuizShell";
import { OptionCard } from "./OptionCard";
import {
  AiButton,
  FeatureEditor,
  FieldLabel,
  MAX_DESCRIPTION_LENGTH,
  MAX_GENERATION_ATTEMPTS,
  persistedUrl,
  PhotoPicker,
  QuizPhoto,
} from "./shared";
import type { QuizProgress } from "./types";

const MAX_PHOTOS = 2; // services: hero + one secondary image

export const SERVICE_TYPES = [
  "Mortgage Broker",
  "Plumbing",
  "Photography",
  "Staging",
  "General Contractor",
  "Cabinetry",
  "Junk Removal",
  "Wholesaler",
  "Electrical",
  "Landscaping",
  "Cleaning",
];

const FEATURE_SUGGESTIONS = [
  "Free estimates",
  "Licensed & insured",
  "Same-day service",
  "Satisfaction guaranteed",
  "Locally owned",
  "24/7 support",
];

type Screen =
  | "mode"
  | "source"
  | "saved"
  | "stype"
  | "describe"
  | "contact"
  | "photos"
  | "features"
  | "subject"
  | "review";

type Source = "saved" | "manual";

const buildFlow = (source: Source | null): Screen[] =>
  source === "saved"
    ? ["mode", "source", "saved"]
    : [
        "mode",
        "source",
        "stype",
        "describe",
        "contact",
        "photos",
        "features",
        "subject",
        "review",
      ];

interface Form {
  title: string;
  serviceType: string;
  description: string;
  subject: string;
  phone: string;
  email: string;
  website: string;
  address: string;
}

const emptyForm: Form = {
  title: "",
  serviceType: "",
  description: "",
  subject: "",
  phone: "",
  email: "",
  website: "",
  address: "",
};

export interface ServiceQuizProps {
  progress: QuizProgress;
  onTotal: (n: number) => void;
  services: any[];
  loadingServices: boolean;
  setMultiMode: (v: boolean) => void;
  onSelect: (service: any) => void;
  onAdd: (service: any) => void;
  onDelete: (id: string | number) => void;
  setCampaignSubject: (s: string) => void;
  onEnhancedImageUpdate: (p: {
    id: string;
    originalUrl: string;
    enhancedUrl: string;
    isHero: boolean;
  }) => void;
  getFullUrl: (u?: string | null) => string;
  onExit: () => void;
  /** Optional: open on a specific screen (defaults to the first question). */
  initialScreen?: string;
}

export const ServiceQuiz: React.FC<ServiceQuizProps> = ({
  progress,
  onTotal,
  services,
  loadingServices,
  setMultiMode,
  onSelect,
  onAdd,
  onDelete,
  setCampaignSubject,
  onEnhancedImageUpdate,
  getFullUrl,
  onExit,
  initialScreen,
}) => {
  const { toast } = useToast();
  const [screen, setScreen] = useState<Screen>((initialScreen as Screen) || "mode");
  const [source, setSource] = useState<Source | null>(null);
  const [form, setForm] = useState<Form>(emptyForm);
  const [photos, setPhotos] = useState<QuizPhoto[]>([]);
  const [features, setFeatures] = useState<string[]>([]);
  const [customType, setCustomType] = useState("");
  const [useCustomType, setUseCustomType] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoCount, setAutoCount] = useState(0);
  const [aiBusy, setAiBusy] = useState(false);
  const [toDelete, setToDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [fromReview, setFromReview] = useState(false);

  const flow = useMemo(() => buildFlow(source), [source]);
  const idx = Math.max(0, flow.indexOf(screen));
  const position = progress.before + idx + 1;
  const total = progress.before + flow.length + progress.after;

  useEffect(() => {
    onTotal(flow.length);
  }, [flow.length, onTotal]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const goNext = () => {
    if (fromReview) {
      setFromReview(false);
      setScreen("review");
      return;
    }
    const next = flow[idx + 1];
    if (next) setScreen(next);
  };
  const goBack = () => {
    if (fromReview) {
      setFromReview(false);
      setScreen("review");
      return;
    }
    if (idx === 0) onExit();
    else setScreen(flow[idx - 1]);
  };
  const editFromReview = (to: Screen) => {
    setFromReview(true);
    setScreen(to);
  };

  /** Mirrors the classic "Auto IQ": improve description, then draft a subject. */
  const autoIq = async () => {
    const desc = form.description.trim();
    if (aiBusy || autoCount >= MAX_GENERATION_ATTEMPTS) return;
    if (desc.length < 10) {
      toast({
        title: "Description too short",
        description: "Please type at least 10 characters before using Auto IQ.",
        variant: "destructive",
      });
      return;
    }
    setAiBusy(true);
    try {
      const d = await axiosInstance.post(endpoints.generate_description, {
        mode: "service",
        description: desc,
        service_type: form.serviceType,
      });
      const improved = d.data?.description?.trim();
      if (!improved) throw new Error("No description returned from AI");
      set("description", improved.slice(0, MAX_DESCRIPTION_LENGTH));
      const s = await axiosInstance.post(endpoints.generate_subject, {
        description: improved,
        service_type: form.serviceType,
      });
      const subj = s.data?.subjects?.[0];
      if (subj) set("subject", subj);
      setAutoCount((c) => c + 1);
      toast({ title: "✨ Auto IQ applied", description: "Description improved and a subject line generated." });
    } catch (e: any) {
      toast({
        title: "Auto IQ failed",
        description: e?.response?.data?.error || "Could not generate content. Please try again.",
        variant: "destructive",
      });
    } finally {
      setAiBusy(false);
    }
  };

  const polish = async (field: "description" | "subject", kind: "improve" | "grammar") => {
    const text = form[field].trim();
    if (!text || aiBusy) return;
    setAiBusy(true);
    try {
      if (kind === "improve") {
        const r = await axiosInstance.post(endpoints.improve_text, { text });
        const out = r.data?.improved_text?.trim();
        if (out) set(field, field === "description" ? out.slice(0, MAX_DESCRIPTION_LENGTH) : out);
      } else {
        const r = await axiosInstance.post(endpoints.correct_grammar, { text });
        const out = r.data?.corrected_text?.trim();
        if (out) set(field, field === "description" ? out.slice(0, MAX_DESCRIPTION_LENGTH) : out);
      }
    } catch {
      toast({ title: "Failed", description: "Could not update the text", variant: "destructive" });
    } finally {
      setAiBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`${endpoints.service_listings}${toDelete.id}/`);
      onDelete(toDelete.id);
      toast({ title: "Service deleted", description: "The service has been removed permanently." });
      setToDelete(null);
    } catch (e: any) {
      toast({
        title: "Error",
        description: e?.response?.data?.detail || "Failed to delete service",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const createService = async () => {
    const missing: string[] = [];
    if (!form.title.trim()) missing.push("Title");
    if (!form.serviceType.trim()) missing.push("Service Type");
    if (!form.description.trim()) missing.push("Description");
    if (!form.subject.trim()) missing.push("Email Subject");
    if (photos.length < 1) missing.push("Images");
    if (features.length < 1) missing.push("Features");
    if (missing.length) {
      toast({
        title: "Missing Required Fields",
        description: `Please fill: ${missing.join(", ")}`,
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const validFeatures = features.map((name) => ({ name }));
      const hasFiles = photos.some((p) => p.file instanceof File);
      let res;

      if (hasFiles) {
        const fd = new FormData();
        fd.append("title", form.title.trim());
        fd.append("headline", form.title.trim());
        fd.append("service_type", form.serviceType || "");
        if (form.description.trim()) fd.append("description", form.description.trim());
        if (form.subject.trim()) fd.append("subject", form.subject.trim());
        if (form.phone.trim()) fd.append("phone", form.phone.trim());
        if (form.email.trim()) fd.append("email", form.email.trim());
        if (form.website.trim()) fd.append("website", form.website.trim());
        if (form.address.trim()) fd.append("address", form.address.trim());
        fd.append("features", JSON.stringify(validFeatures));
        photos.forEach((p, i) => {
          if (p.file instanceof File) {
            if (i === 0) fd.append("hero_image", p.file, p.file.name);
            else if (i === 1) fd.append("photo_1", p.file, p.file.name);
          }
        });
        res = await axiosInstance.post(endpoints.service_listings, fd, {
          headers: { "Content-Type": undefined as any },
          transformRequest: [(d: any) => d],
        });
      } else {
        res = await axiosInstance.post(
          endpoints.service_listings,
          {
            title: form.title.trim(),
            headline: form.title.trim(),
            description: form.description.trim(),
            service_type: form.serviceType || "",
            subject: form.subject.trim(),
            phone: form.phone.trim(),
            email: form.email.trim(),
            website: form.website.trim(),
            address: form.address.trim(),
            features: validFeatures,
            photos: photos
              .filter((p, i) => i > 0 && p.url && !p.url.startsWith("blob:"))
              .map((p) => p.url),
          },
          { headers: { "Content-Type": "application/json" } }
        );
      }

      const server = res.data;
      const localUrls = photos.map((p) => persistedUrl(p.url)).filter(Boolean);
      const hero = localUrls[0] || server.hero_image_url || "";
      const serverSecondary = (server.photos_urls || server.photos || []).filter(
        (u: string) => typeof u === "string" && u.trim() && u.trim() !== hero
      );
      const extra = localUrls.length > 1 ? localUrls.slice(1) : serverSecondary;

      const created = {
        id: String(server.id),
        title: server.title,
        service_type: server.service_type,
        description: server.description || "",
        phone: server.phone || "",
        email: server.email || "",
        website: server.website || "",
        address: server.address || "",
        image: hero,
        hero_image_url: hero,
        photos: extra,
        photos_urls: extra,
        subject: server.subject || "",
        features: server.features || [],
      };

      onAdd(created);
      toast({
        title: "✅ Service created",
        description: `Service added with ${server.features?.length || 0} features!`,
        duration: 1500,
      });
      if (server.subject) setCampaignSubject(server.subject);
      onSelect(created);
    } catch (error: any) {
      let msg = "Failed to add service";
      const data = error?.response?.data;
      if (data) {
        if (data.error) msg = data.error;
        else if (data.detail) msg = data.detail;
        else
          msg = Object.entries(data)
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
            .join("; ");
      }
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const q = { position, total } as const;

  switch (screen) {
    case "mode":
      return (
        <QuizQuestion {...q} title="Is this launch for one service or multi service?" hint="Multi-service launches feature several services in a single email." onBack={onExit} size="md">
          <div className="grid gap-4 sm:grid-cols-2">
            <OptionCard
              icon={Briefcase}
              tone="violet"
              title="Just one service"
              description="A focused launch for a single service."
              onClick={() => {
                setMultiMode(false);
                goNext();
              }}
            />
            <OptionCard
              icon={Layers}
              tone="blue"
              title="Multi service"
              description="Pick multiple services and templates in one launch."
              onClick={() => setMultiMode(true)}
            />
          </div>
        </QuizQuestion>
      );

    case "source":
      return (
        <QuizQuestion {...q} title="How would you like to add your service?" onBack={goBack} size="md">
          <div className="grid gap-4 sm:grid-cols-2">
            <OptionCard
              icon={FolderOpen}
              tone="blue"
              title="Use a saved service"
              description="Pick from services you've already added."
              badge={<Badge variant="secondary">{services.length}</Badge>}
              onClick={() => {
                setSource("saved");
                setScreen("saved");
              }}
            />
            <OptionCard
              icon={PencilLine}
              tone="amber"
              title="Add a new service"
              description="Answer a few quick questions."
              onClick={() => {
                setSource("manual");
                setScreen("stype");
              }}
            />
          </div>
        </QuizQuestion>
      );

    case "saved":
      return (
        <QuizQuestion {...q} title="Which service is this launch for?" hint="Select one of your saved services." onBack={goBack} size="xl">
          {loadingServices ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-28 w-full rounded-2xl" />
              ))}
            </div>
          ) : services.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-white p-10 text-center">
              <Briefcase className="mx-auto mb-3 h-12 w-12 text-slate-300" />
              <p className="font-semibold text-slate-700">No saved services yet</p>
              <Button
                className="mt-5"
                onClick={() => {
                  setSource("manual");
                  setScreen("stype");
                }}
              >
                <Plus className="mr-2 h-4 w-4" /> Add a new service
              </Button>
            </div>
          ) : (
            <div className="max-h-[520px] space-y-3 overflow-y-auto pr-1">
              {services.map((s) => {
                const img = getFullUrl(s.hero_image_url || s.image || s.hero_image || "");
                const fcount = Array.isArray(s.features) ? s.features.length : 0;
                return (
                  <div
                    key={s.id}
                    className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-blue-300 hover:shadow-md sm:flex-row sm:items-center"
                  >
                    <div className="h-24 w-full shrink-0 overflow-hidden rounded-xl bg-slate-100 sm:h-20 sm:w-28">
                      {img ? (
                        <img src={img} alt="" className="h-full w-full object-cover" onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          <Briefcase className="h-7 w-7 text-slate-300" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-base font-semibold text-slate-900">{s.title || "Untitled service"}</p>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {s.service_type && <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">{s.service_type}</Badge>}
                        {fcount > 0 && <Badge variant="secondary">{fcount} features</Badge>}
                      </div>
                      {s.description && <p className="mt-1.5 line-clamp-1 text-sm text-slate-500">{s.description}</p>}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Button variant="outline" size="icon" title="Delete service" onClick={() => setToDelete(s)} className="h-10 w-10 border-red-200 text-red-500 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <Button onClick={() => onSelect(s)} className="h-10 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 px-5 text-white">
                        Use this service
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <Dialog open={!!toDelete} onOpenChange={(o) => !o && !deleting && setToDelete(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete this service?</DialogTitle>
                <DialogDescription>"{toDelete?.title}" will be removed permanently.</DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setToDelete(null)} disabled={deleting}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={confirmDelete} disabled={deleting}>
                  {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </QuizQuestion>
      );

    case "stype":
      return (
        <QuizQuestion
          {...q}
          title="What kind of service do you offer?"
          onBack={goBack}
          onContinue={goNext}
          continueDisabled={!form.serviceType.trim()}
          size="xl"
        >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICE_TYPES.map((t) => (
              <OptionCard
                key={t}
                layout="row"
                icon={Briefcase}
                tone="violet"
                title={t}
                selected={!useCustomType && form.serviceType === t}
                onClick={() => {
                  setUseCustomType(false);
                  set("serviceType", t);
                }}
              />
            ))}
            <OptionCard
              layout="row"
              icon={Plus}
              tone="slate"
              title="Something else"
              selected={useCustomType}
              onClick={() => {
                setUseCustomType(true);
                set("serviceType", customType);
              }}
            />
          </div>
          {useCustomType && (
            <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <FieldLabel required>Your service type</FieldLabel>
              <Input
                autoFocus
                value={customType}
                maxLength={100}
                placeholder="e.g. Pest control"
                onChange={(e) => {
                  setCustomType(e.target.value);
                  set("serviceType", e.target.value);
                }}
                className="h-11"
              />
            </div>
          )}
        </QuizQuestion>
      );

    case "describe":
      return (
        <QuizQuestion
          {...q}
          title="Title and description"
          hint="Name your service, then describe it — Auto IQ can polish it and draft a subject line."
          onBack={goBack}
          onContinue={goNext}
          continueDisabled={!form.title.trim() || !form.description.trim()}
          size="md"
        >
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <FieldLabel required>Service title</FieldLabel>
            <Input
              autoFocus
              value={form.title}
              maxLength={255}
              placeholder="Premium home staging"
              onChange={(e) => set("title", e.target.value)}
              className="h-12 text-base"
            />

            <div className="mt-6">
              <FieldLabel required>Description</FieldLabel>
              <Textarea
                rows={7}
                value={form.description}
                maxLength={MAX_DESCRIPTION_LENGTH}
                placeholder="What do you do, and why should people choose you?"
                onChange={(e) => set("description", e.target.value)}
                className="text-base"
              />
              <div className="mt-1 text-right text-xs text-slate-400">
                {form.description.length}/{MAX_DESCRIPTION_LENGTH}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <AiButton
                  label={autoCount >= MAX_GENERATION_ATTEMPTS ? "Limit reached" : "Auto IQ"}
                  onClick={autoIq}
                  loading={aiBusy}
                  disabled={autoCount >= MAX_GENERATION_ATTEMPTS || form.description.trim().length < 10}
                />
                <AiButton label="Improve" tone="slate" onClick={() => polish("description", "improve")} disabled={!form.description.trim()} />
                <AiButton label="Fix grammar" tone="slate" onClick={() => polish("description", "grammar")} disabled={!form.description.trim()} />
              </div>
            </div>
          </div>
        </QuizQuestion>
      );

    case "contact":
      return (
        <QuizQuestion
          {...q}
          title="How can customers reach you?"
          hint="All optional — shown on your flyer when provided."
          onBack={goBack}
          onContinue={goNext}
          skipLabel="Skip for now"
          onSkip={goNext}
          size="md"
        >
          <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:grid-cols-2">
            <div>
              <FieldLabel>Phone</FieldLabel>
              <Input value={form.phone} maxLength={30} placeholder="(555) 123-4567" onChange={(e) => set("phone", e.target.value)} className="h-11" />
            </div>
            <div>
              <FieldLabel>Email</FieldLabel>
              <Input type="email" value={form.email} placeholder="you@company.com" onChange={(e) => set("email", e.target.value)} className="h-11" />
            </div>
            <div>
              <FieldLabel>Website</FieldLabel>
              <Input value={form.website} placeholder="www.company.com" onChange={(e) => set("website", e.target.value)} className="h-11" />
            </div>
            <div>
              <FieldLabel>Address</FieldLabel>
              <Input value={form.address} placeholder="123 Main St" onChange={(e) => set("address", e.target.value)} className="h-11" />
            </div>
          </div>
        </QuizQuestion>
      );

    case "photos":
      return (
        <QuizQuestion
          {...q}
          title="Add photos for your service"
          hint={`Upload up to ${MAX_PHOTOS} photos. The first is the main image.`}
          onBack={goBack}
          onContinue={goNext}
          continueDisabled={photos.length < 1}
          summary={`${photos.length} of ${MAX_PHOTOS} photos added`}
          size="lg"
        >
          <PhotoPicker photos={photos} max={MAX_PHOTOS} onChange={setPhotos} onEnhanced={onEnhancedImageUpdate} />
        </QuizQuestion>
      );

    case "features":
      return (
        <QuizQuestion
          {...q}
          title="What are your key features?"
          hint="Add at least one feature (up to 10)."
          onBack={goBack}
          onContinue={goNext}
          continueDisabled={features.length < 1}
          size="md"
        >
          <FeatureEditor features={features} onChange={setFeatures} suggestions={FEATURE_SUGGESTIONS} />
        </QuizQuestion>
      );

    case "subject":
      return (
        <QuizQuestion
          {...q}
          title="Write the email subject line"
          hint="This is what recipients see in their inbox."
          onBack={goBack}
          onContinue={goNext}
          continueDisabled={!form.subject.trim()}
          size="md"
        >
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <FieldLabel required>Email subject</FieldLabel>
            <Input
              value={form.subject}
              placeholder="Free estimates this month only"
              onChange={(e) => set("subject", e.target.value)}
              className="h-12 text-base"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              <AiButton label="Improve" tone="slate" onClick={() => polish("subject", "improve")} disabled={!form.subject.trim()} />
              <AiButton label="Fix grammar" tone="slate" onClick={() => polish("subject", "grammar")} disabled={!form.subject.trim()} />
            </div>
          </div>
        </QuizQuestion>
      );

    case "review": {
      const rows: { label: string; value: React.ReactNode; to: Screen }[] = [
        { label: "Service type", value: form.serviceType, to: "stype" },
        { label: "Title", value: form.title, to: "describe" },
        { label: "Subject", value: form.subject, to: "subject" },
        {
          label: "Contact",
          value: [form.phone, form.email, form.website, form.address].filter(Boolean).join(" · ") || "Not provided",
          to: "contact",
        },
      ];
      return (
        <QuizQuestion
          {...q}
          title="Everything look right?"
          hint="Review your service, then we'll save it and move on."
          onBack={goBack}
          onContinue={createService}
          continueLoading={saving}
          continueLabel="Save & use this service"
          size="lg"
        >
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            {photos.length > 0 && (
              <div className="grid grid-cols-2 gap-1">
                {photos.map((p) => (
                  <img key={p.id} src={p.url} alt="" className="h-44 w-full object-cover" />
                ))}
              </div>
            )}
            <dl className="divide-y divide-slate-100">
              {rows.map((r) => (
                <div key={r.label} className="flex items-start justify-between gap-4 px-5 py-3">
                  <div className="min-w-0">
                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">{r.label}</dt>
                    <dd className="break-words text-sm font-medium text-slate-900">{r.value || "—"}</dd>
                  </div>
                  <button type="button" onClick={() => editFromReview(r.to)} className="shrink-0 text-sm font-medium text-blue-600 hover:underline">
                    Edit
                  </button>
                </div>
              ))}
              <div className="flex items-start justify-between gap-4 px-5 py-3">
                <div className="min-w-0">
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">Features</dt>
                  <dd className="mt-1 flex flex-wrap gap-1.5">
                    {features.map((f) => (
                      <span key={f} className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                        {f}
                      </span>
                    ))}
                  </dd>
                </div>
                <button type="button" onClick={() => editFromReview("features")} className="shrink-0 text-sm font-medium text-blue-600 hover:underline">
                  Edit
                </button>
              </div>
              <div className="flex items-start justify-between gap-4 px-5 py-3">
                <div className="min-w-0">
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">Description</dt>
                  <dd className="whitespace-pre-line text-sm text-slate-700">{form.description}</dd>
                </div>
                <button type="button" onClick={() => editFromReview("describe")} className="shrink-0 text-sm font-medium text-blue-600 hover:underline">
                  Edit
                </button>
              </div>
            </dl>
          </div>
        </QuizQuestion>
      );
    }

    default:
      return null;
  }
};
